/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommandOrder, CommandResource, CommandService, tapOnCommandSuccessfullyDone } from '@alfa-client/command-shared';
import { StateResource, isNotNil } from '@common/tech-shared';
import { Injectable, inject } from '@angular/core';
import { Observable, filter } from 'rxjs';
import { VorgangFacade } from './+state/vorgang.facade';
import { VorgangWithEingangLinkRel } from './vorgang.linkrel';
import { VorgangWithEingangResource } from './vorgang.model';
import { VorgangService } from './vorgang.service';
import {
  createAbschliessenCommand,
  createAnnehmenWithAssignCommand,
  createBearbeitenCommand,
  createVerwerfenCommand,
  createWiedereroeffnenCommand,
  createZurueckholenCommand,
  createZurueckholenWithUnassignCommand,
  createZurueckstellenCommand,
} from './vorgang.util';

@Injectable({ providedIn: 'root' })
export class VorgangCommandService {
  private readonly facade = inject(VorgangFacade);
  private readonly vorgangService = inject(VorgangService);
  private readonly commandService = inject(CommandService);

  public annehmen(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.ANNEHMEN_WITH_ASSIGN,
      command: createAnnehmenWithAssignCommand(),
      snackBarErrorMessage: 'Der Vorgang konnte nicht angenommen werden.',
    });
  }

  public bearbeiten(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.BEARBEITEN,
      command: createBearbeitenCommand(),
    });
  }

  public getBeschiedenCommand(): Observable<StateResource<CommandResource>> {
    return this.facade.getStatusCommand(CommandOrder.VORGANG_BESCHEIDEN);
  }

  public verwerfen(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.VERWERFEN,
      command: createVerwerfenCommand(),
    });
  }

  public wiedereroeffnen(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.WIEDEREROEFFNEN,
      command: createWiedereroeffnenCommand(),
    });
  }

  public zurueckholen(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.ZURUECKHOLEN,
      command: createZurueckholenCommand(),
    });
  }

  public zurueckholenWithUnassign(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.ZURUECKHOLEN_WITH_UNASSIGN,
      command: createZurueckholenWithUnassignCommand(),
    });
  }

  public zurueckstellen(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.ZURUECKSTELLEN,
      command: createZurueckstellenCommand(),
    });
  }

  public abschliessen(vorgang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.doReloadAfter(
      this.commandService.createCommand({
        resource: vorgang,
        linkRel: VorgangWithEingangLinkRel.ABSCHLIESSEN,
        command: createAbschliessenCommand(),
      }),
    );
  }

  doReloadAfter(statusCommand: Observable<StateResource<CommandResource>>): Observable<StateResource<CommandResource>> {
    return statusCommand.pipe(
      filter(isNotNil),
      tapOnCommandSuccessfullyDone((commandStateResource: StateResource<CommandResource>) =>
        this.vorgangService.reloadVorgang(commandStateResource.resource),
      ),
    );
  }

  public getRevokeCommand(): Observable<StateResource<CommandResource>> {
    return this.facade.getRevokeCommand();
  }
}
